'use client';

import React, { useEffect } from 'react';
import { usePathname } from 'next/navigation';

import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { getOrganizationAnalyticsProperties, useConfig } from '@/contexts/ConfigContext';
import { useAppSelector } from '@/lib/hooks';
import FeatureTrackingService, {
  setOrganizationAnalyticsProperties,
} from '@/services/analytics/feature-tracking-service';
import { isSharedSpacePath } from '@/services/nav/public-routes';
import { EffectiveRole, role } from 'auth-types';

interface PostHogProviderProps {
  children: React.ReactNode;
}

let posthogInitialized = false;
type AnalyticsUserRole = 'student' | 'instructor' | 'admin';

const resolveAnalyticsUserRole = (
  effectiveRole: EffectiveRole | null | undefined,
  fallbackRole: role,
): AnalyticsUserRole | null => {
  if (effectiveRole?.roleType === 'student' || fallbackRole === 'student') {
    return 'student';
  }

  if (
    effectiveRole?.permissions?.perm_enterprise_exclusive === true ||
    fallbackRole === 'enterprise_admin'
  ) {
    return 'admin';
  }

  if (effectiveRole?.roleType === 'admin' || fallbackRole === 'instructor') {
    return 'instructor';
  }

  return null;
};

export function PostHogProvider({ children }: PostHogProviderProps) {
  const pathname = usePathname();
  const config = useConfig()?.config ?? null;
  const loggedIn = useAppSelector((state) => state.isLoggedIn.status);
  const userRole = useAppSelector((state) => state.isLoggedIn.role);
  const effectiveRole = useAppSelector((state) => state.isLoggedIn.effectiveRole);

  // Initialise PostHog once on the client
  useEffect(() => {
    if (posthogInitialized) return;

    const isSharedRoute = isSharedSpacePath(window.location.pathname);

    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      person_profiles: 'always',
      autocapture: false,
      capture_dead_clicks: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_performance: { web_vitals: false },
      capture_exceptions: false,
      disable_session_recording: isSharedRoute,
      before_send: (event) => (isSharedSpacePath(window.location.pathname) ? null : event),
    });
    if (!isSharedRoute) {
      posthog.startSessionRecording();
    }
    if (process.env.NODE_ENV === 'development') {
      // Log detailed PostHog debug output in the console
      posthog.debug();
    }
    posthogInitialized = true;
  }, []);

  useEffect(() => {
    if (!posthogInitialized) return;

    if (isSharedSpacePath(pathname)) {
      posthog.stopSessionRecording();
    } else {
      posthog.startSessionRecording();
    }
  }, [pathname]);

  useEffect(() => {
    posthog.register({ current_product: 'assistiv' });

    if (!loggedIn) {
      posthog.unregister('user_role');
      return;
    }

    const analyticsUserRole = resolveAnalyticsUserRole(effectiveRole, userRole);
    if (!analyticsUserRole) {
      return;
    }

    posthog.register({
      user_role: analyticsUserRole,
      current_product: 'assistiv',
    });
  }, [effectiveRole, loggedIn, userRole]);

  useEffect(() => {
    return FeatureTrackingService.startDeadClickTracking();
  }, []);

  // Adding analytic fields for all events, based on organization config (e.g. organization_id, organization_name)
  useEffect(() => {
    if (!config) {
      return;
    }

    const organizationProperties = getOrganizationAnalyticsProperties(
      window.location.hostname,
      config,
    );
    setOrganizationAnalyticsProperties(organizationProperties);

    posthog.register(organizationProperties);

    if (!organizationProperties.organization_id) {
      posthog.unregister('organization_id');
    }
  }, [config]);

  return <PHProvider client={posthog}>{children}</PHProvider>;
}
